import { Component, computed, effect, inject, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardComponent } from '@sportello-balneari/shared-components';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { EMPTY, of, switchMap } from 'rxjs';
import { extractApiMessage } from '../../../api/api-error-message';
import { CompilingApiService } from '../../../api/compiling-api.service';
import {
  Applicant,
  TenderFormAnswerEvaluationProgress,
} from '../../../api/interfaces';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import {
  EvaluationPollingService,
  isEvaluationInProgress,
} from '../../../services/compiling/evaluation-polling';
import { EvaluationRequestService } from '../../../services/compiling/evaluation-request.service';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { SAVE_BEFORE_EVALUATION_PROVIDER } from '../../../services/save-before-evaluation.provider';
import { ResultAdministrativeOfferCardComponent } from './cards/administrative-offer-card/administrative-offer-card.component';
import { ResultAnalysesCardComponent } from './cards/analyses-card/analyses-card.component';
import { ResultAreasCardComponent } from './cards/areas-card/areas-card.component';
import { ResultEconomicOfferCardComponent } from './cards/economic-offer-card/economic-offer-card.component';
import { ResultIncoherenceReportCardComponent } from './cards/incoherence-report-card/incoherence-report-card.component';
import { ResultProgressCardComponent } from './cards/progress-card/progress-card.component';
import { ResultStrategicPlanCardComponent } from './cards/strategic-plan-card/strategic-plan-card.component';

@Component({
  selector: 'lib-result',
  imports: [
    CardComponent,
    ButtonModule,
    ResultProgressCardComponent,
    ResultAnalysesCardComponent,
    ResultAreasCardComponent,
    ResultStrategicPlanCardComponent,
    ResultAdministrativeOfferCardComponent,
    ResultIncoherenceReportCardComponent,
    ResultEconomicOfferCardComponent,
    ProgressSpinner,
  ],
  templateUrl: './result.html',
  styleUrl: './result.css',
  providers: [EvaluationPollingService],
})
export class Result {
  private readonly tenderContext = inject(TenderContextService);
  private readonly compilingApiService = inject(CompilingApiService);
  protected readonly permissions = inject(AnswerPermissionsService);
  private readonly polling = inject(EvaluationPollingService);
  private readonly evaluationRequests = inject(EvaluationRequestService);
  private readonly messageService = inject(MessageService);
  private readonly saveProvider = inject(SAVE_BEFORE_EVALUATION_PROVIDER, {
    optional: true,
  });

  private readonly tenderFormAnswer = computed(() =>
    this.tenderContext.tenderFormAnswer(),
  );

  readonly tenderFormAnswerId = computed(() => this.tenderFormAnswer()?.id);

  readonly applicant = computed(
    () => this.tenderFormAnswer()?.applicant as Applicant | undefined,
  );

  readonly tenderInfo = computed(() => {
    const info = this.tenderContext.tenderInfo();
    if (!info) return null;
    return {
      attachments: info.attachments,
      expiryDate: info.expiryDate,
      publicationDate: info.publicationDate,
    };
  });

  readonly resultResource = rxResource({
    params: () => this.tenderFormAnswerId(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.compilingApiService.getResult(params);
    },
  });

  readonly evaluationRequested = signal(false);
  readonly evaluationProgress =
    signal<TenderFormAnswerEvaluationProgress | null>(null);

  constructor() {
    // Anything else on the page that wants an evaluation raises it here rather
    // than calling the endpoint, so request-poll-reload stays in one place.
    this.evaluationRequests.requests$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.requestEvaluation());

    effect(() => {
      const id = this.tenderFormAnswerId();
      if (!id || this.evaluationRequested()) return;

      this.compilingApiService
        .getEvaluationProgress(id)
        .subscribe((progress) => {
          if (isEvaluationInProgress(progress.status)) {
            this.evaluationProgress.set(progress);
            this.evaluationRequested.set(true);
            this.startPolling(id);
          }
        });
    });

    effect(() => {
      const value = this.resultResource.value();
      this.tenderContext.evaluationResult.set(value ?? null);
    });
  }

  reload() {
    this.resultResource.reload();
  }

  /**
   * Asks for a new evaluation, saving the form first so the scoring reads what
   * is on screen.
   *
   * The failure branch is not decoration: the endpoint refuses for reasons the
   * user can act on and the backend writes them out — a demo whose evaluation
   * allowance is spent comes back **429** with a sentence saying so. Without a
   * handler the button silently did nothing, which reads as a broken page
   * rather than as an answer.
   */
  requestEvaluation() {
    const id = this.tenderFormAnswerId();
    if (!id) return;

    const save$ = this.saveProvider
      ? this.saveProvider.saveBeforeEvaluation()
      : of(undefined);

    save$
      .pipe(switchMap(() => this.compilingApiService.requestEvaluation(id)))
      .subscribe({
        next: () => {
          this.evaluationRequested.set(true);
          this.startPolling(id);
        },
        error: (err: unknown) => {
          const message = extractApiMessage(err);

          this.messageService.add({
            // A refusal the backend explained is something the user can do
            // something about; anything else is a failure on our side.
            severity: message ? 'warn' : 'error',
            summary: message ? 'Valutazione non avviata' : 'Errore',
            detail:
              message ??
              'Si è verificato un errore durante la richiesta di valutazione.',
            life: 6000,
          });
        },
      });
  }

  private startPolling(id: string) {
    this.polling
      .poll(() => this.compilingApiService.getEvaluationProgress(id))
      .subscribe((progress) => {
        this.evaluationProgress.set(progress);

        if (progress.status === 'Completed') {
          this.evaluationRequested.set(false);
          this.evaluationProgress.set(null);
          this.resultResource.reload();
        }
      });
  }
}
